import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClockRotateLeft, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FundingTemplateUsageEntry } from '../../api/fundingApi'
import { useStyles } from './TemplateUsagesModal.styles'

type Props = {
  open: boolean
  onClose: () => void
  entries: FundingTemplateUsageEntry[]
  loading?: boolean
  /** Navigate to the funding request detail when the request link is clicked. */
  onOpenRequest?: (entry: FundingTemplateUsageEntry) => void
}

const humanizeStatus = (status?: string | null) => {
  const raw = (status || '').trim()
  if (!raw) return '—'
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase().replace(/_/g, ' ')
}

export const TemplateUsagesModal = ({
  open,
  onClose,
  entries,
  loading = false,
  onOpenRequest,
}: Props) => {
  const { classes, cx } = useStyles()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      data-testid="template-usages-modal"
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.titleGroup}>
            <span className={classes.titleIcon}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>
            <Typography component="h2" className={classes.title}>
              Template used in following funding requests
            </Typography>
          </div>
        </div>
        <button
          type="button"
          className={classes.close}
          aria-label="Close"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className={classes.table}>
        <div className={classes.headerRow}>
          <div className={cx(classes.headerCell, classes.colCompany)}>
            Company name
          </div>
          <div className={cx(classes.headerCell, classes.colRequest)}>
            Funding request
          </div>
          <div className={cx(classes.headerCell, classes.colCreatedBy)}>
            Created by
          </div>
          <div className={cx(classes.headerCell, classes.colStatus)}>
            Status
          </div>
        </div>
        {loading ? (
          <Typography component="p" className={classes.empty}>
            Loading…
          </Typography>
        ) : entries.length === 0 ? (
          <Typography component="p" className={classes.empty}>
            This template has not been used in any funding requests yet.
          </Typography>
        ) : (
          entries.map((entry, index) => {
            const requestTitle =
              entry.funding_request_title?.trim() ||
              entry.opportunity_id?.trim() ||
              entry.program_label?.trim() ||
              entry.workflow_id
            return (
              <div
                key={entry.workflow_id}
                className={cx(
                  classes.row,
                  index % 2 === 0 ? classes.rowAlt : classes.rowPlain
                )}
                data-testid="template-usage-row"
              >
                <div className={classes.cellCompany}>
                  {entry.company_name?.trim() || '—'}
                </div>
                <div className={classes.cellRequest}>
                  {onOpenRequest ? (
                    <button
                      type="button"
                      className={classes.requestLink}
                      onClick={() => onOpenRequest(entry)}
                    >
                      {requestTitle}
                    </button>
                  ) : (
                    requestTitle
                  )}
                </div>
                <div className={classes.cellCreatedBy}>
                  {entry.created_by?.trim() || '—'}
                </div>
                <div className={classes.cellStatus}>
                  {humanizeStatus(entry.status)}
                </div>
              </div>
            )
          })
        )}
      </div>
    </Dialog>
  )
}

export default TemplateUsagesModal
